// frontend/src/pages/P2PMarket.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import USDTMarket from "./p2p/market/USDTMarket";
import BTCMarket from "./p2p/market/BTCMarket";
import ETHMarket from "./p2p/market/ETHMarket";

// To add more coins: create XYZMarket.jsx and append to COIN_TABS
const COIN_TABS = [
  { key: "USDT", label: "USDT", Component: USDTMarket },
  { key: "BTC", label: "BTC", Component: BTCMarket },
  { key: "ETH", label: "ETH", Component: ETHMarket },
];

export default function P2PMarket() {
  const [intent, setIntent] = useState("sell"); // 'sell' | 'buy'
  const [activeCoinKey, setActiveCoinKey] = useState("USDT");

  // detect mobile to tighten spacing without touching child components
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 640px)");
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);

  const ActiveCoinMarket =
    COIN_TABS.find((t) => t.key === activeCoinKey)?.Component || USDTMarket;

  return (
    <motion.div
      // IMPORTANT: extra bottom padding for phone bottom tab bar in P2PHub
      className={`w-full bg-white dark:bg-slate-950 ${
        isMobile ? "pb-24" : "pb-10"
      }`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.28 }}
    >
      <div className="w-full mx-auto px-0 sm:px-6">
        <div className="bg-white dark:bg-slate-950 overflow-visible border-t-4 border-blue-500">
          {/* Compact header (smaller + professional) */}
          <header className="px-3 sm:px-6 py-3 sm:py-6 border-b border-blue-100 dark:border-slate-800">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[12px] sm:text-sm text-blue-700/80 dark:text-blue-300/80 mt-0.5">
                  Browse live offers
                </p>
              </div>

              {/* Tiny right-side context badge (desktop only) */}
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-blue-700/80 dark:text-blue-300/80">
                  Mode:
                </span>
                <span className="px-2 py-1 rounded-lg border border-blue-200 dark:border-slate-700 text-xs font-semibold text-blue-900 dark:text-blue-200">
                  {intent.toUpperCase()} • {activeCoinKey}
                </span>
              </div>
            </div>
          </header>

          <main className="px-0 sm:px-4 lg:px-6 py-2 sm:py-4 space-y-2 sm:space-y-4">
            {/* Sticky filter bar (Binance-like compact) */}
            <motion.nav
              role="tablist"
              aria-label="Coin selector and intent"
              className={[
                "sticky top-0 z-20",
                "bg-white/90 dark:bg-slate-950/90 supports-[backdrop-filter]:backdrop-blur",
                "border-b border-blue-100 dark:border-slate-800",
                "px-2 sm:px-3 py-2",
              ].join(" ")}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.22 }}
            >
              <div className="flex items-center justify-between gap-2">
                {/* Coin pills: horizontal scroll on mobile */}
                <div
                  className={[
                    "flex items-center gap-2",
                    "overflow-x-auto scrollbar-hide",
                    "min-w-0",
                    isMobile ? "pr-2" : "",
                  ].join(" ")}
                  style={{
                    WebkitOverflowScrolling: "touch",
                    overscrollBehavior: "contain",
                  }}
                >
                  <div className="flex gap-2 min-w-max">
                    {COIN_TABS.map((tab) => {
                      const active = activeCoinKey === tab.key;
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setActiveCoinKey(tab.key)}
                          className={[
                            "rounded-xl border font-semibold transition",
                            isMobile ? "px-3 py-1.5 text-[12px]" : "px-3.5 py-2 text-sm",
                            active
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-blue-900 border-blue-200 hover:bg-blue-50 dark:bg-slate-900 dark:text-blue-200 dark:border-slate-700 dark:hover:bg-slate-800",
                          ].join(" ")}
                        >
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Buy/Sell compact */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setIntent("buy")}
                    aria-pressed={intent === "buy"}
                    className={[
                      "rounded-xl border font-semibold transition",
                      isMobile ? "px-3 py-1.5 text-[12px]" : "px-3.5 py-2 text-sm",
                      intent === "buy"
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-blue-900 border-blue-200 hover:bg-blue-50 dark:bg-slate-900 dark:text-blue-200 dark:border-slate-700 dark:hover:bg-slate-800",
                    ].join(" ")}
                  >
                    Buy
                  </button>

                  <button
                    type="button"
                    onClick={() => setIntent("sell")}
                    aria-pressed={intent === "sell"}
                    className={[
                      "rounded-xl border font-semibold transition",
                      isMobile ? "px-3 py-1.5 text-[12px]" : "px-3.5 py-2 text-sm",
                      intent === "sell"
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-blue-900 border-blue-200 hover:bg-blue-50 dark:bg-slate-900 dark:text-blue-200 dark:border-slate-700 dark:hover:bg-slate-800",
                    ].join(" ")}
                  >
                    Sell
                  </button>
                </div>
              </div>

              {/* Mobile summary line (compact) */}
              <div className="mt-2 text-[11px] sm:hidden text-blue-700/80 dark:text-blue-300/80">
                Showing{" "}
                <strong className="text-blue-900 dark:text-blue-200">
                  {intent.toUpperCase()}
                </strong>{" "}
                offers in{" "}
                <strong className="text-blue-900 dark:text-blue-200">
                  {activeCoinKey}
                </strong>
              </div>
            </motion.nav>

            {/* Market list (full-bleed, no card wrapper) */}
            <motion.section
              key={`${activeCoinKey}-${intent}`}
              id={`panel-${activeCoinKey}`}
              role="tabpanel"
              className="w-full"
              initial={{ y: 6, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.2 }}
              aria-live="polite"
            >
              <div
                className={[
                  "w-full text-slate-900 dark:text-slate-100",
                  isMobile ? "text-[13px] leading-5 px-2" : "px-0",
                ].join(" ")}
              >
                <ActiveCoinMarket
                  intent={intent}
                  compact={isMobile}
                  dense={isMobile}
                  frameless
                  fullBleed
                />
              </div>
            </motion.section>
          </main>
        </div>
      </div>
    </motion.div>
  );
}
